import { useEffect, useRef, useState } from 'react'

import classNames from 'classnames'
import { checkCookies, setCookies } from 'cookies-next'

import { Trans } from '@lingui/macro'
import Button from 'Root/App/Button'
import Input from 'Root/App/Form/Input'
import Paragraph from 'Root/App/Paragraph'
import Title from 'Root/App/Title'
import Logo from 'Root/Component/Header/Logo'
import CheckInbox from 'Root/Component/check-inbox'
import CreatorInviteInfo from 'Root/Component/creator-invite-info'
import SignInInfo from 'Root/Component/sign-in-info'
import SocialLoginButton from 'Root/Component/social-login-button'
import { AUTHENTICATION_PROVIDERS } from 'lib/constants'
import { getAccessLink } from 'lib/get-access-link'
import { CreatorInfo } from 'lib/get-element-preview'
import getQueryString from 'lib/get-query-string'
import { checkEmailProvider } from 'lib/utils'
import Link from 'next/link'
import { useRouter } from 'next/router'

const LoginPanelHeader = () => (
  <>
    <Title size="M" className="text-center">
      <Trans>Condividi contenuti digitali, da professionista.</Trans>
    </Title>
    <Title size="XS" className="text-gray-400">
      <Trans>Accedi al tuo account Taylora</Trans>.
    </Title>
  </>
)

const LoginPanel = ({
  creatorInfo,
  onClickCallback,
}: {
  creatorInfo: CreatorInfo
  onClickCallback: Function
}) => {
  const { query, push } = useRouter()
  const [showSignInError, setShowSignInError] = useState(false)
  const [showAccessError, setShowAccessError] = useState(false)
  const [showInvalidEmailProviderError, setShowInvalidEmailProviderError] =
    useState(false)
  const [disableSubmit, setDisableSubmit] = useState(false)

  const prefill_email = query.prefill_email
    ? getQueryString(query.prefill_email)
    : ''
  const [hideForPrefillGiven, setHideForPrefilGiven] = useState(
    prefill_email !== ''
  )
  const creatorName = creatorInfo?.brandName
    ? creatorInfo?.brandName
    : creatorInfo?.firstname
  const creatorAvatar = creatorInfo?.brandLogoSmall
    ? creatorInfo?.brandLogoSmall
    : creatorInfo?.picture

  async function handleSubmit(e) {
    e.preventDefault()

    const form = e.target
    const formJson = Object.fromEntries(new FormData(form).entries())

    setDisableSubmit(true)

    const res = await getAccessLink(
      window.location.hostname,
      formJson['email'] as string,
      getQueryString(query.returnUrl),
      onClickCallback,
      () => setShowAccessError(true),
      () => setShowSignInError(true)
    )

    setDisableSubmit(res)
  }

  const handleEmailInputChange = (e) => {
    const email = e.target.value

    if (checkEmailProvider(email)) {
      setShowInvalidEmailProviderError(true)
      return
    } else {
      setShowInvalidEmailProviderError(false)
    }
  }

  const handleGetAccessLink = async () => {
    setDisableSubmit(true)

    const res = await getAccessLink(
      window.location.hostname,
      prefill_email,
      getQueryString(query.returnUrl),
      onClickCallback,
      () => {
        setShowAccessError(true)
        setHideForPrefilGiven(false)
      },
      () => {
        setShowSignInError(true)
        setHideForPrefilGiven(false)
      }
    )

    setDisableSubmit(res)
  }

  useEffect(() => {
    if (prefill_email) {
      handleGetAccessLink()
    }
  }, [])

  return (
    <div
      className={classNames([
        'flex flex-col items-center gap-6 px-4 lg:px-6 mx-auto',
        hideForPrefillGiven ? 'hidden' : '',
      ])}
      style={{ maxWidth: '408px' }}
    >
      {creatorInfo ? (
        <CreatorInviteInfo
          creatorName={creatorName}
          creatorPicture={creatorAvatar}
        />
      ) : (
        <LoginPanelHeader />
      )}

      {!creatorInfo?.hasCustomDomain && (
        <ul id="#social-login-btns" className="flex flex-col gap-2 w-full">
          {AUTHENTICATION_PROVIDERS.map((provider) => (
            <li key={`social-login-btn-${provider}`}>
              <SocialLoginButton
                provider={provider}
                onClick={() =>
                  push({
                    pathname: `/auth/redirect/${provider}`,
                    query: { returnUrl: getQueryString(query.returnUrl) },
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}

      <hr className="border-t-2 bg-gray-200 w-20 mt-6"></hr>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit}>
        <span id="input-box" className="flex flex-col gap-2 w-full">
          <label>
            <Paragraph>
              <Trans>La tua email</Trans>
            </Paragraph>
          </label>
          <Input
            name="email"
            type="email"
            onChange={handleEmailInputChange}
            required
          />

          {showSignInError && (
            <span id="error-field">
              <Paragraph className="text-red-500 text-sm">
                <Trans>Oops, non riusciamo a trovarti.</Trans>
              </Paragraph>
            </span>
          )}

          {showAccessError && (
            <span id="access-error-field">
              <Paragraph className="text-red-500 text-sm">
                <Trans>Oops, si è verificato un errore.</Trans>
              </Paragraph>
            </span>
          )}

          {showInvalidEmailProviderError && (
            <span id="access-error-field">
              <Paragraph className="text-red-500 text-sm">
                <Trans>
                  Abbiamo riscontrato problemi di ricezione legati al tuo
                  provider di posta. Ti invitiamo a contattare il nostro
                  supporto o ad utilizzare un altro indirizzo email.
                </Trans>
              </Paragraph>
            </span>
          )}
        </span>

        <span>
          <Button
            type="submit"
            className={classNames([
              'bg-gray-900 w-full',
              { 'animate-pulse': disableSubmit },
            ])}
            disabled={disableSubmit}
          >
            <Trans>Continua</Trans>
          </Button>

          <SignInInfo
            locale={getQueryString(query.locale)}
            className="mt-1 text-center"
          />
        </span>
      </form>
    </div>
  )
}

const Login = ({ creatorInfo }: { creatorInfo: CreatorInfo }) => {
  const [invoked, setInvoked] = useState(false)
  const [checkInbox, setCheckInbox] = useState(false)
  const insertEmailRef = useRef(null)

  const hasBrand = creatorInfo?.brandName || creatorInfo?.brandLogoSmall

  useEffect(() => {
    if (invoked) {
      return
    }
    const now = new Date()

    const urlParams = new URLSearchParams(window.location.search)

    if (!checkCookies('after_login_redirect')) {
      setCookies(
        'after_login_redirect',
        urlParams.get('ref') || window.location.href,
        {
          httpOnly: false,
          expires: new Date(now.setUTCMonth(now.getUTCMonth() + 12)),
        }
      )
    }

    setInvoked(true)
  }, [invoked, setInvoked])

  return (
    <div className="flex h-screen">
      <section
        id="login-panel"
        className={`flex flex-col w-full relative items-center bg-white gap-16 ${
          checkInbox ? 'justify-center' : 'justify-between'
        }`}
      >
        <span className="relative mb-auto top-10">
          {!hasBrand && (
            <Link href="/">
              <Logo />
            </Link>
          )}
        </span>

        <div
          ref={insertEmailRef}
          className={`transition-opacity duration-200 ${
            !checkInbox ? 'opacity-100' : 'opacity-0'
          } `}
          onTransitionEnd={() =>
            (insertEmailRef.current.style.display = 'none')
          }
        >
          <LoginPanel
            creatorInfo={creatorInfo}
            onClickCallback={() => setCheckInbox(true)}
          />
        </div>

        <div
          className={`absolute transition-all duration-500 ${
            checkInbox ? 'opacity-100' : 'opacity-0'
          } `}
          style={{
            transform: checkInbox ? 'translateY(0)' : 'translateY(-100%)',
          }}
        >
          <CheckInbox />
        </div>

        {/* Needed to keep panel aligned center, and logo start */}
        <span className="mt-auto"></span>
      </section>
    </div>
  )
}

export default Login
